import React, { useEffect, useState } from "react";
import { Loader2, X, Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import NotesList from "@/components/shownotes/NotesList";
import { downloadPdf } from "@/components/shownotes/download";

const ALL = "all";

// Published notes, filterable by production group and date range; directors can download the filtered set as one PDF.
export default function ArchiveTab({ productions, onOpen, reload, canDownload, onDelete }) {
  const [pid, setPid] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = { ...(pid !== ALL && { production_id: pid }), ...(from && { date_from: from }), ...(to && { date_to: to }) };

  useEffect(() => {
    api.get("/show-notes/archive", { params }).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, [pid, from, to, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadAll = async () => { setBusy(true); await downloadPdf("/show-notes/archive/pdf", params, "show-notes.pdf"); setBusy(false); };
  const filtered = pid !== ALL || from || to;
  return (
    <div>
      <Card className="mb-5 flex flex-wrap items-center gap-3 border-slate-200 p-4 shadow-sm" data-testid="notes-archive-filters">
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger className="w-60" data-testid="notes-archive-production"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} data-testid="notes-archive-production-all">All productions</SelectItem>
            {productions.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`notes-archive-production-${p.id}`}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">From
          <Input type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="notes-archive-from" />
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">To
          <Input type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="notes-archive-to" />
        </label>
        {filtered && (
          <Button variant="ghost" size="sm" className="rounded-full text-slate-500" onClick={() => { setPid(ALL); setFrom(""); setTo(""); }} data-testid="notes-archive-clear">
            <X className="mr-1 h-3.5 w-3.5" /> Clear
          </Button>
        )}
        {canDownload && !!items?.length && (
          <Button size="sm" variant="outline" className="ml-auto rounded-full" disabled={busy} onClick={downloadAll} data-testid="notes-archive-download-all">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />} Download all ({items.length})
          </Button>
        )}
      </Card>
      {!items ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <NotesList items={items} onOpen={onOpen} testId="notes-archive-list" onDelete={onDelete} canDelete={() => !!onDelete}
          empty={filtered ? ["No notes match these filters", "Try another production or date range."] : ["No published notes yet", "Approved Rehearsal and Show Notes will appear here."]} />
      )}
    </div>
  );
}
